// src/components/diary/WorkspacesManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Building2, Users, User, Plus, Settings, ChevronRight, ChevronDown,
  Briefcase, GraduationCap, Church, Heart, Landmark, Building, Edit3,
  Trash2, UserPlus, Calendar, Target, FolderTree, Shield, Clock,
  MoreVertical, Check, X, Loader2, AlertCircle, Crown, Eye, Lock
} from 'lucide-react';
import { workspaceService, WORKSPACE_TYPES, ORGANISATION_TYPES, TEAM_TYPES, MEMBER_ROLES } from '../../services/workspaceService';
import { firestoreService } from '../../services/firestoreService';
import { UserService } from '../../services/userService';

// Icon mapping
const ORG_ICONS = {
  business: Building2,
  school: GraduationCap,
  church: Church,
  npo: Heart,
  government: Landmark,
  cooperative: Users,
  agency: Briefcase,
  other: Building
};

// --- WORKSPACE LIST ITEM ---
const WorkspaceListItem = ({ workspace, isExpanded, onToggle, onEdit, onDelete, onManageMembers, teams, departments, user }) => {
  const [showMenu, setShowMenu] = useState(false);
  const isOwner = workspace.ownerId === user?.uid;
  const isPersonal = workspace.type === WORKSPACE_TYPES.PERSONAL;
  const OrgIcon = ORG_ICONS[workspace.organisationType] || Building2;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Workspace Header */}
      <div 
        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 ${
          workspace.type === WORKSPACE_TYPES.PERSONAL ? 'bg-blue-50' :
          workspace.type === WORKSPACE_TYPES.ORGANISATION ? 'bg-purple-50' : 'bg-green-50'
        }`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
          
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            workspace.type === WORKSPACE_TYPES.PERSONAL ? 'bg-blue-100' :
            workspace.type === WORKSPACE_TYPES.ORGANISATION ? 'bg-purple-100' : 'bg-green-100'
          }`}>
            {workspace.type === WORKSPACE_TYPES.PERSONAL ? (
              <User className="w-5 h-5 text-blue-600" />
            ) : workspace.type === WORKSPACE_TYPES.ORGANISATION ? (
              <OrgIcon className="w-5 h-5 text-purple-600" />
            ) : (
              <Users className="w-5 h-5 text-green-600" />
            )}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{workspace.name}</span>
              {isOwner && <Crown className="w-3 h-3 text-amber-500" />}
            </div>
            <span className="text-xs text-gray-500">
              {workspace.type === WORKSPACE_TYPES.PERSONAL ? 'Personal Workspace' :
               workspace.type === WORKSPACE_TYPES.ORGANISATION ? ORGANISATION_TYPES[workspace.organisationType]?.label || 'Organisation' :
               TEAM_TYPES[workspace.teamType]?.label || 'Team'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {workspace.memberIds?.length > 1 && (
            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
              {workspace.memberIds.length} members
            </span>
          )}
          
          {!isPersonal && (
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 rounded-lg hover:bg-white/50"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
              
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  <button 
                    onClick={() => { onEdit(workspace); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Edit3 className="w-4 h-4" /> Edit Settings
                  </button>
                  <button 
                    onClick={() => { onManageMembers(workspace); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <UserPlus className="w-4 h-4" /> Manage Members
                  </button>
                  {isOwner && (
                    <button 
                      onClick={() => { onDelete(workspace); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-white p-4 space-y-4">
          {workspace.description && (
            <p className="text-sm text-gray-600">{workspace.description}</p>
          )}

          {/* Departments */}
          {workspace.type === WORKSPACE_TYPES.ORGANISATION && departments.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Departments / Units</h5>
              <div className="flex flex-wrap gap-2">
                {departments.map(dept => (
                  <span key={dept.id} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                    {dept.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Teams under Organisation */}
          {workspace.type === WORKSPACE_TYPES.ORGANISATION && teams.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Teams</h5>
              <div className="space-y-2">
                {teams.map(team => (
                  <div key={team.id} className="flex items-center gap-2 text-sm p-2 bg-green-50 rounded-lg">
                    <Users className="w-4 h-4 text-green-600" />
                    <span className="text-gray-800">{team.name}</span>
                    <span className="text-xs text-gray-500">({team.memberIds?.length || 1} members)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team Schedule */}
          {workspace.type === WORKSPACE_TYPES.TEAM && workspace.schedule?.endDate && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>Ends: {new Date(workspace.schedule.endDate).toLocaleDateString()}</span>
              {workspace.schedule.autoDisband && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Auto-disband</span>
              )}
            </div>
          )}

          {/* Working Hours for Orgs */}
          {workspace.type === WORKSPACE_TYPES.ORGANISATION && workspace.workingHours && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>{workspace.workingHours.start} - {workspace.workingHours.end}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- CREATE WORKSPACE MODAL ---
const CreateWorkspaceModal = ({ type, onClose, onSave, parentOrganisations = [], bookId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organisationType: 'business',
    teamType: 'standing',
    parentOrganisationId: '',
    departmentId: '',
    startDate: '',
    endDate: '',
    autoDisband: false,
    email: '',
    phone: '',
    address: '',
    website: '',
    workingHoursStart: '09:00',
    workingHoursEnd: '17:00',
    workingDays: [1, 2, 3, 4, 5]
  });
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (formData.parentOrganisationId) {
      loadDepartments(formData.parentOrganisationId);
    }
  }, [formData.parentOrganisationId]);

  const loadDepartments = async (orgId) => {
    const depts = await workspaceService.getDepartments(bookId, orgId);
    setDepartments(depts);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving workspace:', error);
    } finally {
      setSaving(false);
    }
  };

  const isOrg = type === 'organisation';
  const isTeam = type === 'team';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isOrg ? 'Create Organisation' : 'Create Team / Group'}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isOrg ? 'Organisation Name' : 'Team Name'} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isOrg ? 'e.g., Sunrise High School' : 'e.g., SMT, Event Committee'}
            />
          </div>

          {/* Description / Purpose */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isTeam ? 'Purpose' : 'Description'}
            </label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isTeam ? 'What is this team for?' : 'Brief description'}
            />
          </div>

          {/* Organisation Type */}
          {isOrg && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Organisation Type</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.values(ORGANISATION_TYPES).map(orgType => {
                  const Icon = ORG_ICONS[orgType.id];
                  return (
                    <button
                      key={orgType.id}
                      onClick={() => setFormData(prev => ({ ...prev, organisationType: orgType.id }))}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        formData.organisationType === orgType.id 
                          ? 'border-purple-500 bg-purple-50 text-purple-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{orgType.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Team Type */}
          {isTeam && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Team Type</label>
              <div className="space-y-2">
                {Object.values(TEAM_TYPES).map(teamType => (
                  <button
                    key={teamType.id}
                    onClick={() => setFormData(prev => ({ ...prev, teamType: teamType.id }))}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      formData.teamType === teamType.id 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      formData.teamType === teamType.id ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {formData.teamType === teamType.id && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{teamType.label}</div>
                      <div className="text-xs text-gray-500">{teamType.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Parent Organisation for Teams */}
          {isTeam && parentOrganisations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Belongs To (Optional)
              </label>
              <select
                value={formData.parentOrganisationId}
                onChange={e => setFormData(prev => ({ ...prev, parentOrganisationId: e.target.value, departmentId: '' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Standalone Team</option>
                {parentOrganisations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Department for Teams */}
          {isTeam && formData.parentOrganisationId && departments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department (Optional)</label>
              <select
                value={formData.departmentId}
                onChange={e => setFormData(prev => ({ ...prev, departmentId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No specific department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Schedule for Time-bound Teams */}
          {isTeam && ['project', 'committee', 'taskforce'].includes(formData.teamType) && (
            <div className="p-4 bg-amber-50 rounded-lg space-y-3">
              <h5 className="font-medium text-amber-900 text-sm">Duration (Optional)</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={e => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.autoDisband}
                  onChange={e => setFormData(prev => ({ ...prev, autoDisband: e.target.checked }))}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-sm text-gray-700">Auto-disband on end date</span>
              </label>
            </div>
          )}

          {/* Contact Details for Organisations */}
          {isOrg && (
            <div className="space-y-3">
              <h5 className="font-medium text-gray-900 text-sm">Contact Details</h5>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Email"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <input
                type="text"
                value={formData.address}
                onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="url"
                value={formData.website}
                onChange={e => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="Website"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          )}

          {/* Working Hours for Organisations */}
          {isOrg && (
            <div className="space-y-3">
              <h5 className="font-medium text-gray-900 text-sm">Working Hours</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start</label>
                  <input
                    type="time"
                    value={formData.workingHoursStart}
                    onChange={e => setFormData(prev => ({ ...prev, workingHoursStart: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End</label>
                  <input
                    type="time"
                    value={formData.workingHoursEnd}
                    onChange={e => setFormData(prev => ({ ...prev, workingHoursEnd: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                  <button
                    key={day}
                    onClick={() => {
                      const days = formData.workingDays.includes(index)
                        ? formData.workingDays.filter(d => d !== index)
                        : [...formData.workingDays, index];
                      setFormData(prev => ({ ...prev, workingDays: days }));
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      formData.workingDays.includes(index)
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!formData.name.trim() || saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Create {isOrg ? 'Organisation' : 'Team'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- DEPARTMENTS MANAGER ---
const DepartmentsManager = ({ bookId, workspaceId, departments, onRefresh }) => {
  const [newDept, setNewDept] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddDepartment = async () => {
    if (!newDept.name.trim()) return;
    setSaving(true);
    try {
      await workspaceService.createDepartment(bookId, workspaceId, newDept);
      setNewDept({ name: '', description: '' });
      onRefresh();
    } catch (error) {
      console.error('Error adding department:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDepartment = async (deptId) => {
    if (!editValue.trim()) return;
    try {
      await workspaceService.updateDepartment(bookId, deptId, { name: editValue });
      setEditingId(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating department:', error);
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      await workspaceService.deleteDepartment(bookId, deptId);
      onRefresh();
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  return (
    <div className="space-y-3">
      <h5 className="font-medium text-gray-900 flex items-center gap-2">
        <FolderTree className="w-4 h-4" /> Departments / Units
      </h5>
      
      {/* Add New */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newDept.name}
          onChange={e => setNewDept(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Department name"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <button
          onClick={handleAddDepartment}
          disabled={!newDept.name.trim() || saving}
          className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {departments.map(dept => (
          <div key={dept.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
            {editingId === dept.id ? (
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  autoFocus
                />
                <button onClick={() => handleUpdateDepartment(dept.id)} className="text-green-600 hover:text-green-700">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <span className="text-sm text-gray-700">{dept.name}</span>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => { setEditingId(dept.id); setEditValue(dept.name); }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button 
                    onClick={() => handleDeleteDepartment(dept.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
        {departments.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-2">No departments yet</p>
        )}
      </div>
    </div>
  );
};

// --- MEMBERS MODAL ---
const MembersModal = ({ workspace, members, bookId, user,onClose, onRefresh, contacts = [] }) => {
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [adding, setAdding] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');

  const isWorkspaceOwner = workspace.ownerId === user?.uid;

  const handleAddMember = async () => {
    const email = selectedContact || newEmail;
    if (!email.trim()) return;
    
    setAdding(true);
    try {
      const trimmedEmail = email.trim();
      
      // Lookup user by email in pb-users
      const existingUsers = await UserService.searchUsersByEmail(trimmedEmail);
      const existingUser = existingUsers.length > 0 ? existingUsers[0] : null;
      
      await workspaceService.addMember(bookId, workspace.id, {
        userId: existingUser?.id || `pending-${Date.now()}`,
        email: trimmedEmail,
        displayName: existingUser?.displayName || trimmedEmail.split('@')[0],
        photoURL: existingUser?.photoURL || null,
        role: newRole,
        status: existingUser ? 'active' : 'pending'
      });
      
      setNewEmail('');
      setSelectedContact('');
      onRefresh();
    } catch (error) {
      console.error('Error adding member:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.displayName || member.email}?`)) return;
    try {
      await workspaceService.removeMember(bookId, workspace.id, member.id, member.userId);
      onRefresh();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await workspaceService.updateMember(bookId, member.id, { role: newRole });
      onRefresh();
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Manage Members - {workspace.name}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Add Member */}
          <div className="space-y-3">
            <h5 className="font-medium text-gray-900">Add Member</h5>
            
            {/* From Contacts */}
            {contacts.length > 0 && (
              <select
                value={selectedContact}
                onChange={e => { setSelectedContact(e.target.value); setNewEmail(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Select from contacts...</option>
                {contacts.filter(c => c.email).map(contact => (
                  <option key={contact.id} value={contact.email}>
                    {contact.name} ({contact.email})
                  </option>
                ))}
              </select>
            )}
            
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="flex-1 border-t border-gray-200" />
              or enter email
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={e => { setNewEmail(e.target.value); setSelectedContact(''); }}
                placeholder="Email address"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <select
                value={newRole}
                onChange={e => setNewRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {Object.values(MEMBER_ROLES).filter(r => r.id !== 'owner').map(role => (
                  <option key={role.id} value={role.id}>{role.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleAddMember}
              disabled={(!newEmail.trim() && !selectedContact) || adding}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Add Member
            </button>
          </div>

          {/* Members List */}
          <div className="space-y-2">
            <h5 className="font-medium text-gray-900">Current Members ({members.length})</h5>
            {members.map(member => (
              <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-medium">
                    {(member.displayName || member.email)?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{member.displayName}</div>
                    <div className="text-xs text-gray-500">{member.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {member.role === 'owner' ? (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Owner
                    </span>
                  ) : (
                    <>
                      <select
                        value={member.role}
                        onChange={e => handleRoleChange(member, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        {Object.values(MEMBER_ROLES).filter(r => r.id !== 'owner').map(role => (
                          <option key={role.id} value={role.id}>{role.label}</option>
                        ))}
                      </select>
                      {isWorkspaceOwner && (
                      <button
                        onClick={() => handleRemoveMember(member)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN WORKSPACES MANAGER ---
export const WorkspacesManager = ({ bookId, user, contacts = [] }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [createType, setCreateType] = useState(null);
  const [membersWorkspace, setMembersWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [departmentsMap, setDepartmentsMap] = useState({});

  useEffect(() => {
    if (bookId) loadWorkspaces();
  }, [bookId]);

  const loadWorkspaces = async () => {
    setLoading(true);
    try {
      const data = await workspaceService.getWorkspaces(bookId);
      setWorkspaces(data);

      // Load teams for each org
      const teams = {};
      const depts = {};
      for (const ws of data.filter(w => w.type === WORKSPACE_TYPES.ORGANISATION)) {
        teams[ws.id] = await workspaceService.getOrganisationTeams(bookId, ws.id);
        depts[ws.id] = await workspaceService.getDepartments(bookId, ws.id);
      }
      setTeamsMap(teams);
      setDepartmentsMap(depts);
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async (workspace) => {
    const data = await workspaceService.getMembers(bookId, workspace.id);
    setMembers(data);
    setMembersWorkspace(workspace);
  };

  const handleCreateOrganisation = async (formData) => {
    await workspaceService.createOrganisation(bookId, formData);
    loadWorkspaces();
  };

  const handleCreateTeam = async (formData) => {
    await workspaceService.createTeam(bookId, formData);
    loadWorkspaces();
  };

  const handleDeleteWorkspace = async (workspace) => {
    if (!window.confirm(`Delete "${workspace.name}"? This cannot be undone.`)) return;
    try {
      await workspaceService.deleteWorkspace(bookId, workspace.id);
      loadWorkspaces();
    } catch (error) {
      console.error('Error deleting workspace:', error);
    }
  };

  const organisations = workspaces.filter(w => w.type === WORKSPACE_TYPES.ORGANISATION);
  const standaloneTeams = workspaces.filter(w => w.type === WORKSPACE_TYPES.TEAM && !w.parentOrganisationId);
  const personalWorkspace = workspaces.find(w => w.type === WORKSPACE_TYPES.PERSONAL);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Workspaces</h3>
          <p className="text-sm text-gray-500">Manage your personal, organisation, and team workspaces</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCreateType('organisation')}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            <Building2 className="w-4 h-4" /> New Organisation
          </button>
          <button
            onClick={() => setCreateType('team')}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <Users className="w-4 h-4" /> New Team
          </button>
        </div>
      </div>

      {/* Personal Workspace */}
      {personalWorkspace && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Personal</h4>
          <WorkspaceListItem
            workspace={personalWorkspace}
            isExpanded={expandedId === personalWorkspace.id}
            onToggle={() => setExpandedId(expandedId === personalWorkspace.id ? null : personalWorkspace.id)}
            onEdit={() => {}}
            onDelete={() => {}}
            onManageMembers={() => {}}
            teams={[]}
            departments={[]}
            user={user}
          />
        </div>
      )}

      {/* Organisations */}
      {organisations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Organisations</h4>
          <div className="space-y-3">
            {organisations.map(org => (
              <WorkspaceListItem
                key={org.id}
                workspace={org}
                isExpanded={expandedId === org.id}
                onToggle={() => setExpandedId(expandedId === org.id ? null : org.id)}
                onEdit={(ws) => {/* TODO: Edit modal */}}
                onDelete={handleDeleteWorkspace}
                onManageMembers={loadMembers}
                teams={teamsMap[org.id] || []}
                departments={departmentsMap[org.id] || []}
                user={user}
              />
            ))}
          </div>
        </div>
      )}

      {/* Standalone Teams */}
      {standaloneTeams.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Standalone Teams / Groups</h4>
          <div className="space-y-3">
            {standaloneTeams.map(team => (
              <WorkspaceListItem
                key={team.id}
                workspace={team}
                isExpanded={expandedId === team.id}
                onToggle={() => setExpandedId(expandedId === team.id ? null : team.id)}
                onEdit={(ws) => {/* TODO: Edit modal */}}
                onDelete={handleDeleteWorkspace}
                onManageMembers={loadMembers}
                teams={[]}
                departments={[]}
                user={user}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {organisations.length === 0 && standaloneTeams.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="font-medium text-gray-900 mb-1">No organisations or teams yet</h4>
          <p className="text-sm text-gray-500 mb-4">Create your first workspace to start collaborating</p>
        </div>
      )}

      {/* Create Modal */}
      {createType && (
        <CreateWorkspaceModal
          type={createType}
          onClose={() => setCreateType(null)}
          onSave={createType === 'organisation' ? handleCreateOrganisation : handleCreateTeam}
          parentOrganisations={organisations}
          bookId={bookId}
        />
      )}

      {/* Members Modal */}
      {membersWorkspace && (
        <MembersModal
          workspace={membersWorkspace}
          members={members}
          user={user}
          bookId={bookId}
          onClose={() => { setMembersWorkspace(null); setMembers([]); }}
          onRefresh={() => loadMembers(membersWorkspace)}
          contacts={contacts}
        />
      )}
    </div>
  );
};

export default WorkspacesManager;